import { SxProps, Typography } from "@mui/material";
import { createStyles } from "@mui/styles";
import {
  CSSProperties,
  MouseEventHandler,
  ReactNode,
  isValidElement,
} from "react";
import { NavLinkProps, NavLink as RRDNavLink } from "react-router-dom";
import { palette } from "../../../styles/theme";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import { Box } from "../Box";
import { AdminIcon, Icon } from "../Icon";
import { MaterialSymbolType } from "../Icon/IconConstant";
import { useAuth } from "../../hooks/useAuth";
import { PropsWithDataTest } from "../../../types";

const styles = createStyles({
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    textDecoration: "none",
    userSelect: "none",
  },
  activeActionItem: {
    background: palette.blue.pale,
    "&:hover": {
      background: palette.blue.pale,
    },
  },
  actionItem: {
    width: "100%",
    padding: "8px",
    borderRadius: "4px",
    fontStyle: "normal",
    fontWeight: "normal",

    color: "black",
    display: "flex",
    flexDirection: "row",
    cursor: "pointer",

    alignItems: "center",
    gap: "8px",
    "&:hover": {
      background: palette.blue.paleEffect,
    },
  },
});

interface Props
  extends PropsWithDataTest<Omit<NavLinkProps, "onClick" | "to" | "content">> {
  startIcon?: MaterialSymbolType | ReactNode;
  endIcon?: MaterialSymbolType | ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement | HTMLAnchorElement>;
  content: string | ReactNode;
  contentStyles?: SxProps;
  noActiveStyle?: boolean;
  isActive?: boolean;
  clickableLink?: boolean;
  adminOnly?: boolean;
  to?: string;
}

export const NavLink = ({
  startIcon,
  endIcon,
  content,
  contentStyles,
  noActiveStyle,
  isActive,
  clickableLink,
  adminOnly,
  "data-test": dataTest,
  ...props
}: Props) => {
  const { isMobile } = useThemeBreakpoints();
  const { isAdmin } = useAuth();

  const getContent = (_isActive: boolean) => (
    <Box
      sx={[
        styles.container,
        styles.actionItem,
        (_isActive || isActive) && !noActiveStyle
          ? styles.activeActionItem
          : null,
      ]}
      onClick={(e) => {
        if (!clickableLink) {
          props.onClick?.(e);
        }
      }}
      data-test={dataTest}
    >
      {Boolean(startIcon) && (
        <Box>
          {isValidElement(startIcon) ? (
            startIcon
          ) : (
            <Icon
              icon={startIcon as MaterialSymbolType}
              size={isMobile ? 18 : 22}
            />
          )}
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        alignItems="center"
      >
        {typeof content === "string" ? (
          <Typography variant="actionSmallIcon" sx={contentStyles}>
            {content}
          </Typography>
        ) : (
          content
        )}
        {(Boolean(endIcon) || adminOnly) && (
          <Box>
            {isValidElement(endIcon) ? (
              endIcon
            ) : (
              <Icon
                icon={endIcon as MaterialSymbolType}
                size={isMobile ? 18 : 22}
              />
            )}
          </Box>
        )}
        {adminOnly && <AdminIcon />}
      </Box>
    </Box>
  );

  if (adminOnly && !isAdmin) {
    return null;
  }

  return props.onClick && !clickableLink ? (
    <Box sx={props.style as CSSProperties}>{getContent(false)}</Box>
  ) : (
    <RRDNavLink
      {...props}
      to={props.to ?? ""}
      style={{ ...(styles.container as CSSProperties), ...props.style }}
    >
      {(opts) => getContent(opts.isActive)}
    </RRDNavLink>
  );
};
